import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import brSvg from "../assets/br.svg";
import gbSvg from "../assets/gb.svg";

const SwitcherContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const SelectButton = styled.button`
  background: white;
  border: 1px solid #e1e1e1;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const OptionsContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: 60px;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transform: translateY(${(props) => (props.$isOpen ? "0" : "10px")});
  transition: all 0.3s ease;
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
`;

const OptionButton = styled.button<{ $active: boolean }>`
  background: ${(props) => (props.$active ? "#f0f0f0" : "transparent")};
  border: none;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  font-family: inherit;
  font-weight: 500;
  color: #333;
  width: 100%;
  white-space: nowrap;

  &:hover {
    background: #f5f5f5;
  }

  img {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

export const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const lang = i18n.language || "en";
  const isPtMode =
    lang.toLowerCase().startsWith("pt") || lang.toLowerCase().startsWith("br");
  const currentLang = isPtMode ? "pt" : "en";

  const toggleOpen = () => setIsOpen(!isOpen);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  const getFlagSrc = (lang: string) => {
    return lang === "pt" ? brSvg : gbSvg;
  };

  return (
    <SwitcherContainer>
      <OptionsContainer $isOpen={isOpen}>
        <OptionButton
          $active={currentLang === "en"}
          onClick={() => changeLanguage("en")}
        >
          <img src={getFlagSrc("en")} alt="English" />
          EN
        </OptionButton>
        <OptionButton
          $active={currentLang === "pt"}
          onClick={() => changeLanguage("pt")}
        >
          <img src={getFlagSrc("pt")} alt="Português" />
          PT-BR
        </OptionButton>
      </OptionsContainer>

      <SelectButton onClick={toggleOpen} aria-label="Change language">
        <img src={getFlagSrc(currentLang)} alt="Current Language" />
      </SelectButton>
    </SwitcherContainer>
  );
};

